'use client';

import { cn } from '@/shared/utils/formatting';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { type ReactNode, memo, useMemo } from 'react';
import { CAROUSEL_TRANSITION_MS, useCarousel } from './hooks/use-carousel';

interface CarouselRowProps<T> {
  /** Accessible name of the row; also the visible heading unless `titleNode` is set. */
  title: string;
  /** Visual heading content (e.g. a brand lockup); `title` stays the accessible name. */
  titleNode?: ReactNode;
  href?: string;
  items: T[];
  renderItem: (item: T, index: number, isClone: boolean) => ReactNode;
  getKey: (item: T) => string | number;
  /** 'landscape' (default, 16:9) or 'portrait' (9:16 shorts) */
  aspectRatio?: 'landscape' | 'portrait';
  /**
   * 'large' shows fewer, bigger landscape cards per page; 'small' shows more, smaller
   * portrait cards (see `data-size` in globals.css).
   */
  size?: 'default' | 'large' | 'small';
  /** When true, carousel fits inside an already-inset parent (no full-bleed). */
  contained?: boolean;
  onReachEnd?: () => void;
  onPageChange?: (page: number, direction: 'next' | 'prev', visibleCount: number) => void;
  /** Control stacked between the title and the rail, e.g. a segmented control. */
  headerAction?: ReactNode;
  /** Turns the rail into the tabpanel of a `headerAction` tablist. */
  tabPanel?: { id: string; labelledBy: string };
  /** Forwarded to `useCarousel`: a new value snaps back to the first page. */
  resetKey?: string;
}

function CarouselRowInner<T>({
  title,
  titleNode,
  href,
  items,
  renderItem,
  getKey,
  aspectRatio = 'landscape',
  size = 'default',
  contained,
  onReachEnd,
  onPageChange,
  headerAction,
  tabPanel,
  resetKey,
}: CarouselRowProps<T>) {
  const {
    visibleCount,
    isInitialState,
    goNext,
    goPrev,
    trackRef,
    translateX,
    shouldAnimate,
    handleTransitionEnd,
    canPaginate,
    clonesMounted,
    mountClones,
    touchHandlers,
  } = useCarousel({
    itemCount: items.length,
    ...(contained ? { contained } : {}),
    ...(onReachEnd !== undefined ? { onReachEnd } : {}),
    ...(onPageChange !== undefined ? { onPageChange } : {}),
    ...(resetKey !== undefined ? { resetKey } : {}),
  });

  // Build track items: [lastPageClone] [all items] [firstPageClone]
  const trackItems = useMemo(() => {
    if (!canPaginate || !clonesMounted) {
      return items.map((item, i) => ({ item, index: i, isClone: false }));
    }

    const result: { item: T; index: number; isClone: boolean }[] = [];

    // Clone of last page (prepended) — vc+1 items for peek content
    const lastPageStart = Math.max(0, items.length - visibleCount - 1);
    for (let i = lastPageStart; i < items.length; i++) {
      const item = items[i];
      if (!item) continue;
      result.push({ item, index: i, isClone: true });
    }

    // All real items
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item) continue;
      result.push({ item, index: i, isClone: false });
    }

    // Clone of first page (appended) — vc+1 items for peek content
    for (let i = 0; i < Math.min(visibleCount + 1, items.length); i++) {
      const item = items[i];
      if (!item) continue;
      result.push({ item, index: i, isClone: true });
    }

    return result;
  }, [items, canPaginate, clonesMounted, visibleCount]);

  const showPrev = !isInitialState && canPaginate;
  const showNext = canPaginate;

  if (items.length === 0) return null;

  return (
    <section className="relative">
      {/* Header */}
      <div
        className={cn(
          'flex mb-4',
          headerAction ? 'flex-col items-start gap-3' : 'items-center justify-between',
          !contained && 'page-px'
        )}
      >
        {href ? (
          <Link
            href={href}
            scroll={false}
            className="group flex items-center gap-2 text-lg md:text-xl font-semibold text-white hover:text-red-primary transition-colors"
          >
            {title}
            <ChevronRight className="w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
          </Link>
        ) : (
          <h2 className="text-lg! md:text-xl! font-semibold! text-white">
            {titleNode ? (
              <>
                <span className="sr-only">{title}</span>
                {titleNode}
              </>
            ) : (
              title
            )}
          </h2>
        )}
        {headerAction}
      </div>

      {/* Carousel container */}
      <div
        className={cn(
          'relative group/carousel overflow-hidden',
          contained
            ? canPaginate && 'carousel-contained'
            : canPaginate
              ? 'carousel-edge'
              : 'rail-inset'
        )}
        {...(canPaginate && isInitialState && !contained ? { 'data-initial': '' } : {})}
        {...(canPaginate ? { onPointerEnter: mountClones, onFocus: mountClones } : {})}
        {...touchHandlers}
        tabIndex={0}
        {...(tabPanel
          ? { id: tabPanel.id, role: 'tabpanel', 'aria-labelledby': tabPanel.labelledBy }
          : { role: 'region', 'aria-label': `${title} carousel` })}
      >
        {/* Prev arrow */}
        {showPrev && (
          <button
            onClick={goPrev}
            className={cn(
              'absolute top-0 bottom-0 left-0 z-20',
              'flex items-center justify-center',
              'bg-black/30 hover:bg-black/60',
              'text-white/60 hover:text-white',
              'transition-all duration-200',
              'hidden md:flex',
              'opacity-0 group-hover/carousel:opacity-100'
            )}
            style={{ width: '48px' }}
            aria-label="Previous page"
            tabIndex={-1}
          >
            <ChevronLeft className="w-8 h-8 lg:w-10 lg:h-10" />
          </button>
        )}

        {/* Next arrow */}
        {showNext && (
          <button
            onClick={goNext}
            className={cn(
              'absolute top-0 bottom-0 right-0 z-20',
              'flex items-center justify-center',
              'bg-black/30 hover:bg-black/60',
              'text-white/60 hover:text-white',
              'transition-all duration-200',
              'hidden md:flex',
              'opacity-0 group-hover/carousel:opacity-100'
            )}
            style={{ width: '48px' }}
            aria-label="Next page"
            tabIndex={-1}
          >
            <ChevronRight className="w-8 h-8 lg:w-10 lg:h-10" />
          </button>
        )}

        {/* Track */}
        <div
          ref={trackRef}
          className="carousel-track"
          {...(aspectRatio === 'portrait' ? { 'data-aspect': 'portrait' } : {})}
          {...(size === 'default' ? {} : { 'data-size': size })}
          style={{
            transform: `translateX(${translateX}px)`,
            transitionProperty: shouldAnimate ? 'transform' : 'none',
            transitionDuration: `${CAROUSEL_TRANSITION_MS}ms`,
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {trackItems.map(({ item, index, isClone }, trackIndex) => (
            <div
              key={isClone ? `clone-${trackIndex}` : getKey(item)}
              className="carousel-item"
              {...(isClone ? { 'aria-hidden': true, inert: true } : {})}
            >
              {renderItem(item, index, isClone)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export const CarouselRow = memo(CarouselRowInner) as typeof CarouselRowInner;
